import { Editor } from '@tiptap/core'
import { NodeViewProps, NodeViewWrapper } from '@tiptap/react'
import { Fragment, useState, type VFC } from 'react'
import { getResourceGlyph } from '@opallabs/gem'
import { useTheme } from '@opallabs/hooks'
import { DotLoader, Icon, Image, PlaceholderText } from '@opallabs/lapidary'
import { EmbeddedResourceAttributes } from '../..'
import { useEmbeddedResource } from '../useEmbeddedResource'
import { useResourceColor } from '../useResourceColor'
import * as styles from './styles'

export const Card: VFC<NodeViewProps> = ({ editor, node }) => {
  const theme = useTheme()
  const { href, objectId, objectType } =
    node.attrs as EmbeddedResourceAttributes

  return (
    <NodeViewWrapper
      as="div"
      css={styles.card(theme)}
      className="embedded-resource-card-node-view"
      contentEditable={false}
      data-drag-handle
    >
      <CardNodeContents
        editor={editor}
        href={href}
        objectId={objectId}
        objectType={objectType}
      />
    </NodeViewWrapper>
  )
}

const CardNodeContents: React.VFC<{
  editor: Editor
  href: string
  objectId: string
  objectType: string
}> = ({ editor, href, objectId, objectType }) => {
  const theme = useTheme()
  const nodeId = `embedded-resource-${objectId}`
  const { data: embeddedResource } = useEmbeddedResource(objectId, editor)

  const color = useResourceColor(theme, embeddedResource?.attributes.color)
  const iconGlyph = getResourceGlyph(theme, objectType)

  const [isImageLoaded, setIsImageLoaded] = useState(false)

  return (
    <Fragment>
      <div css={styles.headingWrapper(theme)} id={`${nodeId}--header`}>
        <span className="embedded-resource-icon">
          <Icon
            ariaHidden={true}
            color={color}
            glyph={iconGlyph}
            height={20}
            width={18}
          />
        </span>
        <span className="embedded-resource-text" title={href}>
          {embeddedResource?.attributes.title ?? href}
        </span>
      </div>

      <a
        href={href}
        target="_blank"
        css={styles.snapshotWrapper}
        rel="noreferrer"
        aria-labelledby={`${nodeId}--header`}
      >
        {embeddedResource?.attributes.default_snapshot_url ? (
          <Fragment>
            {!isImageLoaded && <DotLoader />}
            <Image
              alt={embeddedResource.attributes.title ?? href}
              src={embeddedResource.attributes.default_snapshot_url}
              imageFit="contain"
              styleOverrides={styles.snapshot(isImageLoaded, theme)}
              onLoad={() => setIsImageLoaded(true)}
            />
          </Fragment>
        ) : (
          <div css={styles.snapshotPlaceholder}>
            <Icon
              ariaHidden={true}
              glyph="gem-empty-image"
              width={202}
              height={200}
            />
            <div css={styles.snapshotPlaceholderText}>
              <PlaceholderText>
                Content preview does not exist or could not be rendered. Preview
                will appear once content is updated.
              </PlaceholderText>
            </div>
          </div>
        )}
      </a>
    </Fragment>
  )
}
